/**
 * What an API key may do, as Settings ▸ API Keys asks for it and shows it.
 *
 * shared-auth mints a key with `bindings` (one space, the scopes of a catalogue
 * preset — the same `presetBinding` the Members page writes) and a `mode`:
 * `read` strips every write-family scope whatever the preset holds, `write`
 * keeps them. Both are capped server-side to the creator's own grants, so this
 * page can offer the whole catalogue without letting anyone escalate.
 *
 * Until 2026-10-27 the page sent neither: every key was read-only with no
 * space, so the MCP server's create tools (checks, prompt templates, rules)
 * answered 404 `space_not_visible` for every key a person could make here.
 */

import { presetBinding, presetLabel } from '@/lib/grants';
import type { Preset, TenantBinding } from '@/services/tenantApi';

/** The open-space baseline: every space the creator can reach. */
export const ALL_SPACES = '*';

export type KeyMode = 'read' | 'write';

export interface KeyAccessChoice {
  /** A catalogue preset slug ('member' = Reader). */
  preset: string;
  /** A space id, or `ALL_SPACES`. */
  space: string;
  /** Whether the key may change content. Ignored for a preset that cannot. */
  write: boolean;
}

/** The default: the narrowest useful key — read the Knowledge Base, everywhere. */
export const DEFAULT_KEY_ACCESS: KeyAccessChoice = { preset: 'member', space: ALL_SPACES, write: false };

/** True when the preset carries any scope a write-mode key would keep. */
export function presetCanWrite(slug: string, presets: readonly Preset[]): boolean {
  const entry = presets.find((p) => p.slug === slug);
  return !!entry && entry.scopes.some((s) => !s.endsWith(':read'));
}

/** The `bindings` and `mode` of the mint request for a choice. */
export function keyAccessRequest(
  choice: KeyAccessChoice,
  presets: readonly Preset[],
): { bindings: TenantBinding[]; mode: KeyMode } {
  const mode: KeyMode = choice.write && presetCanWrite(choice.preset, presets) ? 'write' : 'read';
  return { bindings: [presetBinding(choice.space, choice.preset, presets)], mode };
}

/**
 * One line for the key list: "Developer · All spaces · can change content".
 * A key minted before bindings existed has none: it reads only, in no space.
 */
export function describeKeyAccess(
  key: { bindings?: TenantBinding[]; mode?: string },
  presets: readonly Preset[],
  spaceName: (spaceId: string) => string,
): string {
  const ability = key.mode === 'write' ? 'can change content' : 'read only';
  const bindings = key.bindings ?? [];
  if (bindings.length === 0) return `No space · ${ability}`;
  const parts = bindings.map((b) => {
    const who = b.preset ? presetLabel(b.preset, presets) : 'Custom access';
    const where = b.space === ALL_SPACES ? 'All spaces' : spaceName(b.space);
    return `${who} · ${where}`;
  });
  return `${parts.join('; ')} · ${ability}`;
}
